import { logger } from "@/modules/logger";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";

export type ObservabilityFlushReason = "session-start" | "interval" | "manual" | "session-end";

interface TranscriptEventPayload {
  channel: number;
  wordCount?: number;
  transcriptLength?: number;
}

export class ObservabilityManager {
  private readonly EARLY_FLUSH_LIMIT = 12; // roughly the first minute at 5s cadence
  private readonly EARLY_FLUSH_INTERVAL_MS = 5_000;
  private readonly LATE_FLUSH_INTERVAL_MS = 60_000;
  private readonly AUDIO_ACTIVITY_THRESHOLD = 0.02; // normalized amplitude (~2%)

  private sessionActive = false;
  private sessionStartedAt = 0;
  private lastFlushAt: number | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private flushCount = 0;

  private audioWindowCount = 0;
  private audioActiveWindowCount = 0;
  private audioSampleCount = 0;
  private lastAudioTimestamp: number | null = null;
  private lastAudioActiveTimestamp: number | null = null;
  private peakAmplitude = 0;
  private lastRms = 0;

  private transcriptCount = 0;
  private lastTranscriptTimestamp: number | null = null;

  constructor(private readonly store: AppStoreAudioCaptureStore) {}

  beginSession(): void {
    this.sessionActive = true;
    this.sessionStartedAt = Date.now();
    this.lastFlushAt = null;
    this.flushCount = 0;
    this.audioWindowCount = 0;
    this.audioActiveWindowCount = 0;
    this.audioSampleCount = 0;
    this.lastAudioTimestamp = null;
    this.lastAudioActiveTimestamp = null;
    this.peakAmplitude = 0;
    this.lastRms = 0;
    this.transcriptCount = 0;
    this.lastTranscriptTimestamp = null;

    this.logSnapshot("session-start");
    this.scheduleNextFlush();
  }

  ingestDesktopSamples(samples: Int16Array): void {
    if (!this.sessionActive || samples.length === 0) return;

    let peak = 0;
    let sumSquares = 0;
    for (let i = 0; i < samples.length; i++) {
      const normalized = samples[i] / 32768;
      const abs = Math.abs(normalized);
      peak = Math.max(peak, abs);
      sumSquares += normalized * normalized;
    }

    const rms = Math.sqrt(sumSquares / samples.length);
    this.ingestAnalyserSample({ peak, rms, sampleCount: samples.length });
  }

  ingestAnalyserSample({
    peak,
    rms,
    sampleCount = 0,
    timestamp,
  }: {
    peak: number;
    rms: number;
    sampleCount?: number;
    timestamp?: number;
  }): void {
    if (!this.sessionActive) return;

    const now = timestamp ?? Date.now();

    this.audioWindowCount += 1;
    this.audioSampleCount += sampleCount;
    this.lastAudioTimestamp = now;
    this.lastRms = rms;
    this.peakAmplitude = Math.max(this.peakAmplitude, peak);

    if (peak >= this.AUDIO_ACTIVITY_THRESHOLD) {
      this.audioActiveWindowCount += 1;
      this.lastAudioActiveTimestamp = now;
    }
  }

  recordTranscript({ channel, wordCount, transcriptLength }: TranscriptEventPayload): void {
    if (!this.sessionActive) return;
    const now = Date.now();
    const firstTranscript = this.transcriptCount === 0;

    this.transcriptCount += 1;
    this.lastTranscriptTimestamp = now;

    if (firstTranscript) {
      logger.info({
        message: "[AudioCaptureObservability] first_transcript",
        info: {
          channel,
          wordCount: wordCount ?? null,
          transcriptLength: transcriptLength ?? null,
          noteId: this.store.activeNoteId,
          recordingId: this.store.currentSession?.recordingId ?? null,
          capturedAudioId: this.store.currentSession?.capturedAudioId ?? this.store.capturedAudioId,
        },
      });
    }
  }

  onSilenceChange(isSilentSince: number | null): void {
    if (!this.sessionActive) return;
    if (isSilentSince === null) {
      this.lastAudioActiveTimestamp = Date.now();
    }
  }

  flush(reason: ObservabilityFlushReason = "manual"): void {
    if (!this.sessionActive) return;

    this.clearTimer();
    this.logSnapshot(reason);
    if (reason !== "session-end") {
      this.scheduleNextFlush();
    }
  }

  endSession(): void {
    if (!this.sessionActive) return;
    this.clearTimer();
    this.logSnapshot("session-end");
    this.sessionActive = false;
  }

  private logSnapshot(reason: ObservabilityFlushReason): void {
    if (!this.sessionActive) return;

    const now = Date.now();
    const session = this.store.currentSession;

    const elapsedSinceStart = now - this.sessionStartedAt;
    const elapsedSinceLast = this.lastFlushAt ? now - this.lastFlushAt : null;

    logger.info({
      message: "[AudioCaptureObservability] heartbeat",
      info: {
        reason,
        flushIndex: this.flushCount + 1,
        elapsedMs: elapsedSinceStart,
        intervalMs: elapsedSinceLast,
        noteId: session?.noteId ?? this.store.activeNoteId,
        recordingId: session?.recordingId ?? null,
        capturedAudioId: session?.capturedAudioId ?? this.store.capturedAudioId,
        engine: session?.engine ?? (this.store.isDesktop ? "desktop" : "web"),
        status: this.store.status,
        durationSeconds: this.store.duration,
        audioWindowCount: this.audioWindowCount,
        audioActiveWindowCount: this.audioActiveWindowCount,
        audioSampleCount: this.audioSampleCount,
        lastAudioTimestamp: this.lastAudioTimestamp,
        lastAudioActiveTimestamp: this.lastAudioActiveTimestamp,
        audioPeakAmplitude: Number(this.peakAmplitude.toFixed(4)),
        audioLastRms: Number(this.lastRms.toFixed(4)),
        silenceSince: this.store.audioIsSilentSince,
        transcriptCount: this.transcriptCount,
        lastTranscriptTimestamp: this.lastTranscriptTimestamp,
      },
    });

    this.lastFlushAt = now;
    this.flushCount += 1;
  }

  private scheduleNextFlush(): void {
    if (!this.sessionActive) return;
    this.clearTimer();
    const interval =
      this.flushCount < this.EARLY_FLUSH_LIMIT
        ? this.EARLY_FLUSH_INTERVAL_MS
        : this.LATE_FLUSH_INTERVAL_MS;

    this.flushTimer = setTimeout(() => {
      this.flush("interval");
    }, interval);
  }

  private clearTimer(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}
